// 列表页的通用零件（库存总览 / 出入库流水 / 采购单 / 采购建议共用）。
// 样子照订单模块（OrdersPage / OrdersTable）抽出来的，建议由主会话统一提取到 src/components：
// Chips、SearchBox、FilterButton、Applied、ViewToggle、Pager、Sentinel、PhoneBar、DataTable、Stats、Overlay。
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/Button';
import { Empty } from '@/components/States';
import k from './ListKit.module.css';

export { k };

/* ------------------------------ 视图 chips ------------------------------ */

export type ChipItem = {
  key: string;
  label: string;
  count?: number;
  active?: boolean;
  onClick?: () => void;
  /** 跳到另一页的 chip（如「出入库流水」「采购建议」），带箭头，和筛选 chip 区分 */
  to?: string;
};

export function Chips({ items, inset }: { items: ChipItem[]; inset?: 'phone' | 'desk' | 'list' }) {
  const ref = useRef<HTMLDivElement>(null);
  const activeKey = items.find((i) => i.active)?.key;
  // 选中的 chip 滚进可见区（横滑条里可能在屏幕外）
  useEffect(() => {
    ref.current?.querySelector(`.${k.on}`)?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [activeKey]);
  return (
    <div ref={ref} className={[k.chips, inset && k[`in_${inset}`]].filter(Boolean).join(' ')}>
      {items.map((c) => {
        const inner = (
          <>
            {c.label}
            {c.count != null && c.count >= 0 && <i className="tnum">{c.count}</i>}
            {c.to && <Icon name="chev" size={14} />}
          </>
        );
        return c.to ? (
          <Link key={c.key} to={c.to} className={`${k.chip} ${k.navChip}`}>{inner}</Link>
        ) : (
          <button key={c.key} type="button" className={`${k.chip} ${c.active ? k.on : ''}`} aria-pressed={!!c.active} onClick={c.onClick}>
            {inner}
          </button>
        );
      })}
    </div>
  );
}

export function FilterButton({ count, onClick, boxed }: { count: number; onClick: () => void; boxed?: boolean }) {
  return (
    <button type="button" className={[k.fbtn, boxed && k.fboxed, count > 0 && k.fon].filter(Boolean).join(' ')} onClick={onClick}>
      <Icon name="filter" />筛选{count > 0 && <span className={k.fbadge}>{count}</span>}
    </button>
  );
}

/* ------------------------------ 搜索 ------------------------------ */

/** 回车才搜（写进 URL）；清空按钮立即生效 */
export function SearchBox({ value, onSearch, placeholder, autoFocus, className }: { value: string; onSearch: (q: string) => void; placeholder: string; autoFocus?: boolean; className?: string }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <form
      className={[k.search, className].filter(Boolean).join(' ')}
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        onSearch(draft.trim());
      }}
    >
      <Icon name="srch" />
      <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} enterKeyHint="search" autoFocus={autoFocus} aria-label={placeholder} />
      {draft && (
        <button type="button" className={k.clearQ} aria-label="清空搜索" onClick={() => { setDraft(''); onSearch(''); }}>
          <Icon name="x" size={14} />
        </button>
      )}
    </form>
  );
}

/* ------------------------------ 已选条件回显 ------------------------------ */

/** 直接写值，不写「字段：值」（文档 23 §3.4） */
export function Applied({ items, onClear }: { items: { key: string; label: string; onRemove: () => void }[]; onClear: () => void }) {
  if (!items.length) return null;
  return (
    <div className={k.applied}>
      {items.map((a) => (
        <button key={a.key} type="button" className={k.achip} onClick={a.onRemove}>
          {a.label}<Icon name="x" size={14} />
        </button>
      ))}
      <button type="button" className={k.clr} onClick={onClear}>清空</button>
    </div>
  );
}

/* ------------------------------ 卡片 / 表格切换 ------------------------------ */

export type ViewMode = 'cards' | 'table';

export function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  return (
    <div className={k.seg} role="group" aria-label="显示方式">
      <button type="button" className={value === 'cards' ? k.segOn : ''} aria-pressed={value === 'cards'} aria-label="卡片" title="卡片" onClick={() => onChange('cards')}>
        <Icon name="cards" />
      </button>
      <button type="button" className={value === 'table' ? k.segOn : ''} aria-pressed={value === 'table'} aria-label="表格" title="表格" onClick={() => onChange('table')}>
        <Icon name="table" />
      </button>
    </div>
  );
}

/* ------------------------------ 分页（桌面 / iPad） ------------------------------ */

export function Pager({ page, pageSize, total, shown, totalPages, onGo, extra }: { page: number; pageSize: number; total: number; shown: number; totalPages: number; onGo: (p: number) => void; extra?: ReactNode }) {
  const from = total ? (page - 1) * pageSize + 1 : 0;
  return (
    <footer className={k.pager}>
      <span className="tnum">第 {from}–{from + Math.max(shown - 1, 0)} 条 / 共 {total} 条</span>
      {extra}
      <span className={k.sp} />
      <Button size="sm" disabled={page <= 1} onClick={() => onGo(page - 1)}>上一页</Button>
      <span className="tnum">{page} / {Math.max(totalPages, 1)}</span>
      <Button size="sm" disabled={page >= totalPages} onClick={() => onGo(page + 1)}>下一页</Button>
    </footer>
  );
}

/* ------------------------------ 滚到底加载（手机） ------------------------------ */

export function Sentinel({ hasNext, loading, count, onMore }: { hasNext: boolean; loading: boolean; count: number; onMore: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const more = useRef(onMore);
  more.current = onMore;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((es) => es[0].isIntersecting && more.current(), { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={k.more}>
      {hasNext ? (loading ? '加载中…' : '上滑加载更多') : count > 0 ? '没有更多了' : ''}
    </div>
  );
}

/* ------------------------------ 手机导航栏 ------------------------------ */

/**
 * 根页：左对齐 24 大标题；二级页：返回 + 17 居中标题（文档 23 §4）。
 * back.to 为空时走浏览器历史，没有历史就回 fallback。
 */
export function PhoneBar({ title, back, actions }: { title: string; back?: { label?: string; fallback: string; /** 固定回到某页（如采购回「应用」），不走历史 */ to?: string }; actions?: ReactNode }) {
  const nav = useNavigate();
  if (!back) {
    return (
      <header className={k.pnb}>
        <h1>{title}</h1>
        {actions}
      </header>
    );
  }
  return (
    <header className={k.snb}>
      <button type="button" className={k.back} onClick={() => (back.to ? nav(back.to) : history.length > 1 ? nav(-1) : nav(back.fallback))} aria-label={`返回${back.label || ''}`}>
        <Icon name="back" size={20} />
        {back.label && <span>{back.label}</span>}
      </button>
      <h1>{title}</h1>
      <div className={k.acts}>{actions}</div>
    </header>
  );
}

/* ------------------------------ 表格 ------------------------------ */

export type Col<T> = {
  key: string;
  title: ReactNode;
  w?: number;
  align?: 'r';
  /** 首列吸左 */
  fix?: boolean;
  render: (row: T) => ReactNode;
  onSort?: () => void;
  sortDirection?: 'ascending' | 'descending';
};

/** 首列吸左、其余横滑；行高 56（--row-h）。rowTone=warn 时整行淡琥珀（如缺供应商的采购建议） */
export function DataTable<T>({ cols, rows, rowKey, onRowClick, selectedKey, isRowSelected, rowTone, groupBy, dense }: { cols: Col<T>[]; rows: T[]; rowKey: (r: T) => string; onRowClick?: (r: T) => void; selectedKey?: string; isRowSelected?: (r: T) => boolean; rowTone?: (r: T) => 'warn' | undefined; groupBy?: (r: T) => string; dense?: boolean }) {
  return (
    <div className={k.tscroll}>
      <table className={[k.tbl, dense && k.dense, onRowClick && k.clickable].filter(Boolean).join(' ')}>
        <colgroup>{cols.map((c) => <col key={c.key} style={{ width: c.w }} />)}</colgroup>
        <thead>
          <tr>{cols.map((c) => <th key={c.key} aria-sort={c.sortDirection || (c.onSort ? 'none' : undefined)} className={[c.fix && k.fix, c.align === 'r' && k.r].filter(Boolean).join(' ')}>{c.onSort ? <button type="button" className={k.sortHead} onClick={c.onSort}>{c.title}<span aria-hidden="true">{c.sortDirection === 'ascending' ? '↑' : c.sortDirection === 'descending' ? '↓' : '↕'}</span></button> : c.title}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, index) => {
            const id = rowKey(r);
            const tone = rowTone?.(r);
            const group = groupBy?.(r);
            return (
              <Fragment key={id}>
              {group && (index === 0 || group !== groupBy?.(rows[index - 1])) && <tr className={k.groupRow}><td colSpan={cols.length}>{group}</td></tr>}
              <tr className={[(id === selectedKey || isRowSelected?.(r)) && k.sel, tone === 'warn' && k.warnRow].filter(Boolean).join(' ') || undefined} onClick={onRowClick ? () => onRowClick(r) : undefined}>
                {cols.map((c) => <td key={c.key} className={[c.fix && k.fix, c.align === 'r' && k.r].filter(Boolean).join(' ')}>{c.render(r)}</td>)}
              </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------ 统计条 ------------------------------ */

export function Stats({ items }: { items: { label: string; value: ReactNode; tone?: 'neg' | 'warn' | 'hl' }[] }) {
  return (
    <div className={k.stats}>
      {items.map((it) => (
        <div key={it.label}>
          <span>{it.label}</span>
          <b className={['tnum', it.tone && k[`st_${it.tone}`]].filter(Boolean).join(' ')}>{it.value}</b>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------ 无权限 ------------------------------ */

/** 路由不带权限守卫（router.tsx 不归本模块），页面自己挡：写清缺哪个权限、找谁开 */
export function Denied({ what, perm }: { what: string; perm: string }) {
  return (
    <div className={k.denied}>
      <Empty icon="lock" title={`没有${what}的权限`}>
        需要「{perm}」权限。请联系管理员在「系统设置 · 角色」里开通，开通后刷新本页即可。
      </Empty>
    </div>
  );
}

/* ------------------------------ 浮层：手机底部 sheet / 其他右侧抽屉 ------------------------------ */

export function Overlay({ sheet, title, onClose, children, footer, headerRight }: { sheet: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; headerRight?: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className={k.scrim} onClick={onClose}>
      <aside className={sheet ? k.sheet : k.drawer} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        {sheet && <div className={k.grab} />}
        <header className={k.ohd}>
          <button type="button" className={k.ox} onClick={onClose} aria-label="关闭"><Icon name="x" size={20} /></button>
          <b>{title}</b>
          <div className={k.ohr}>{headerRight}</div>
        </header>
        <div className={k.obd}>{children}</div>
        {footer && <footer className={k.oft}>{footer}</footer>}
      </aside>
    </div>
  );
}
